import React from 'react';
import { AttachedFile } from '@alucio/aws-beacon-amplify/src/models';
import {
  DNABox,
  DNAButton,
  DNACard,
  DNAText,
  DNAIcon,
  Iffy,
  Stack,
  util,
} from '@alucio/lux-ui/src';
import { DNAChip, DNAFileTypeChip } from 'src/components/DNA';
import { useAppSettings } from 'src/state/context/AppSettings';
import { downloadContentFromCloudfront } from 'src/utils/loadCloudfrontAsset/loadCloudfrontAsset';
import { styles } from 'src/screens/Hubs/EditHub/Widgets/SharedFiles';
import { AssociatedFileDetails, RowProps } from 'src/screens/Hubs/EditHub/Widgets/SharedFilesComponents/SharedFilesProvider'
import { useHubsState } from 'src/state/context/Hubs/HubsStateProvider.proxy';
import { S } from './DocumentRow';

interface AttachedFileRowProps extends RowProps {
  associatedContent: AssociatedFileDetails
}

const AttachedFileRow: React.FC<AttachedFileRowProps> = ({ associatedContent, isLastItem, handleDeleteItem }) => {
  const { isOnline } = useAppSettings()
  const { showFullSizeBtn } = useHubsState()
  const file = associatedContent.associatedFileORM.file as AttachedFile
  const isRequired = associatedContent.isRequired
  const handleDownload = () => {
    if (!isOnline) return
    downloadContentFromCloudfront(file.srcFile.key, file.srcFileName, file.type)
  }
  return (
    <DNACard
      onPress={handleDownload}
      interactive="pressableAlt"
      appearance="flat"
      style={util.mergeStyles(
        undefined,
        { minHeight: 149 },
        [{ minHeight: 113 }, showFullSizeBtn],
        [S.unavailableContent, !isOnline],
        [styles.bottomBorder, !isLastItem],
      )}
    >
      <DNABox alignY="center" style={styles.row}>
        <DNABox
          fill
          alignY="center"
          appearance={showFullSizeBtn ? 'row' : 'col'}
          spacing={showFullSizeBtn ? undefined : 'sm'}
        >
          {/* Thumbnail */}
          <DNABox style={[styles.documentThumbnailBorder, styles.attachedFileThumbnailBox]}>
            <Stack>
              <Stack.Layer>
                <DNABox fill alignX="center" alignY="center" style={styles.attachedFileThumbnailBox}>
                  <DNAIcon.Styled name="file-text" style={styles.attachedFileIcon} />
                </DNABox>
              </Stack.Layer>
              <Stack.Layer anchor="bottomLeft">
                <DNAFileTypeChip orm={associatedContent.associatedFileORM} />
              </Stack.Layer>
            </Stack>
          </DNABox>
          {/* Document title & badges */}
          <DNABox fill appearance="col" alignY="center" spacing="sm">
            <DNAText
              status="primary"
              numberOfLines={2}
              style={{ textDecorationLine: 'underline' }}
            >
              {file.srcFileName}
            </DNAText>
            <DNABox spacing="sm">
              <Iffy is={isRequired}>
                <DNAChip.Beacon variant="REQUIRED" />
              </Iffy>
              <Iffy is={!isOnline}>
                <DNAChip.Beacon testID={`content-not-available-${file.srcFileName}`} variant="CONTENT_UNAVAILABLE" />
                </Iffy>
            </DNABox>
          </DNABox>
        </DNABox>
        {/* Buttons */}
        <DNABox alignY="center" spacing="sm">
          <DNAButton
            appearance="ghost"
            status="subtle"
            iconLeft="download-simple"
            onPress={handleDownload}
          />
          <Iffy is={!isRequired}>
            <DNAButton
              appearance="ghost"
              status={!isOnline ? 'tertiary' : 'subtle'}
              iconLeft="trash"
              onPress={() => handleDeleteItem(associatedContent.associatedFileId, associatedContent)}
            />
          </Iffy>
        </DNABox>
      </DNABox>
    </DNACard>
  )
}

export default AttachedFileRow